/*
  Musterservice — „Als Muster bestellen" auf jeder Stoffseite.

  Aufbau: Im Warenkorb liegt höchstens EIN Posten „Stoffmuster" (0,00 €). Welche
  Stoffe der Kunde sehen will, hängt als Zeilen-Eigenschaft an genau diesem
  Posten: sichtbar „Stoff 1" … „Stoff 5", dazu versteckt
  `_artikelnummern` und die Handle-Liste `_stoffe`, aus der sich die Auswahl
  wieder einlesen lässt. So steht im Warenkorb eine
  Zeile statt fünf, die Bestellung nennt dem Zuschnitt trotzdem jeden Stoff mit
  Artikelnummer, und der Versand kostet einmal 2,00 € (eigenes Versandprofil).

  Der Warenkorb ist die einzige Quelle der Wahrheit — kein localStorage. Ein in
  einem anderen Tab entfernter Posten wäre sonst hier noch „ausgewählt", und der
  Kunde klickt gegen eine Anzeige, die nicht stimmt.
*/
(() => {
  const TRENNER = ';;';
  const FELD = '_stoffe';
  const NUMMERN = '_artikelnummern';

  // Ein gemeinsamer Abruf für alle Knöpfe auf der Seite (verwandte Produkte!),
  // statt je Knopf einer.
  let cartLauf = null;
  function holeCart(frisch) {
    if (frisch || !cartLauf) {
      cartLauf = fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
    }
    return cartLauf;
  }

  const zeileFinden = (cart, variantId) =>
    ((cart && cart.items) || []).find((i) => Number(i.variant_id) === Number(variantId));

  // Zwei Schreibweisen von `_stoffe`:
  //  - alt (bis 28.09.2026): „handle|Name|Artikelnummer;;handle|…" — stand so
  //    im Bestell-Admin, unlesbar für den Zuschnitt;
  //  - neu: nur die Handles, mit „, " getrennt. Der Name steht in „Stoff N",
  //    die Artikelnummern in `_artikelnummern`.
  // Alte Warenkörbe werden beim nächsten Schreiben still umgeschrieben.
  function stoffeLesen(zeile) {
    const props = (zeile && zeile.properties) || {};
    const roh = props[FELD];
    if (!roh) return [];
    const text = String(roh);
    if (text.includes('|') || text.includes(TRENNER)) {
      return text
        .split(TRENNER)
        .filter(Boolean)
        .map((teil) => {
          const [handle, label, sku] = teil.split('|');
          return { handle: handle || '', label: label || handle || '', sku: sku || '' };
        })
        .filter((s) => s.handle);
    }
    const nummern = String(props[NUMMERN] || '').split(',').map((x) => x.trim());
    return text
      .split(',')
      .map((h) => h.trim())
      .filter(Boolean)
      .map((handle, i) => ({
        handle,
        label: props[`Stoff ${i + 1}`] || handle,
        sku: nummern[i] && nummern[i] !== '–' ? nummern[i] : '',
      }));
  }

  // Sichtbare Eigenschaften neu durchnummerieren, damit nach dem Entfernen eines
  // Stoffs keine Lücke („Stoff 1", „Stoff 3") in der Bestellung steht.
  //
  // Nur der Name, keine Artikelnummer: im Warenkorb stehen die Bilder der
  // gewählten Stoffe daneben, „(FAB-LIN-NAT-227)" sagt der Kundschaft nichts und
  // zerreißt die Zeile auf dem Telefon über drei Zeilen. Für den Zuschnitt steht
  // die Nummer weiterhin in `_stoffe`.
  //
  // Reihenfolge der Schlüssel = Reihenfolge in der Bestellung: erst die Namen,
  // dann die Artikelnummern für den Zuschnitt, die Handles (nur für Bilder und
  // das × im Warenkorb) ganz zuletzt.
  function stoffeSchreiben(stoffe) {
    const props = {};
    stoffe.forEach((s, i) => {
      props[`Stoff ${i + 1}`] = s.label;
    });
    props[NUMMERN] = stoffe.map((s) => s.sku || '–').join(', ');
    props[FELD] = stoffe.map((s) => s.handle).join(', ');
    return props;
  }

  // Ist ein schon gewählter Stoff noch als Muster zu haben? Ausverkauft oder aus
  // dem Shop genommen heißt nein — sonst landet er in der Bestellung, und der
  // Zuschnitt sucht einen Stoff, den es nicht mehr gibt. Die Regel selbst steht
  // in snippets/stoffeya-muster-erlaubt.liquid (dieselbe wie für den Knopf).
  // Nur ein klares Nein zählt; ohne Netz bleibt der Stoff drin.
  const pruefungen = new Map();
  function nochZuHaben(handle) {
    if (!pruefungen.has(handle)) {
      pruefungen.set(handle, fetch(`/products/${encodeURIComponent(handle)}?view=musterstatus`)
        // Ohne „Accept: application/json" — damit antwortet Shopify mit seinem
        // eigenen Produkt-JSON statt mit dieser Vorlage.
        .then((r) => {
          if (r.status === 404) return false;
          if (!r.ok) return true;
          return r.text().then((t) => {
            try {
              return JSON.parse(t).verfuegbar !== false;
            } catch (e) {
              return true;
            }
          });
        })
        .catch(() => true));
    }
    return pruefungen.get(handle);
  }

  // Wie meterware.js: wenn das Theme eine Schublade hat, deren Abschnitte
  // gleich mitrendern lassen – sonst bliebe der Warenkorb optisch stehen.
  function schubladeUndAbschnitte() {
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const kann = cart && typeof cart.renderContents === 'function' && typeof cart.getSectionsToRender === 'function';
    return { cart, kann };
  }

  // `still`: für das Aufräumen beim Seitenaufruf. Trades renderContents öffnet
  // die Schublade — der Warenkorb sprang sonst von selbst auf, nur weil ein Stoff
  // inzwischen ausverkauft war. Still wird nur die Zählblase im Kopf erneuert;
  // die Schublade zieht über cartUpdate nach, ohne aufzugehen (cart.js).
  async function schicke(pfad, body, still) {
    const { cart, kann } = schubladeUndAbschnitte();
    if (still) {
      body.sections = ['cart-icon-bubble'];
      body.sections_url = window.location.pathname;
    } else if (kann) {
      body.sections = cart.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
    }
    const res = await fetch(pfad, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`${pfad}: ${res.status}`);
    const daten = await res.json();
    if (still) {
      const blase = document.getElementById('cart-icon-bubble');
      const html = daten && daten.sections && daten.sections['cart-icon-bubble'];
      if (blase && html) {
        const neu = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
        if (neu) blase.innerHTML = neu.innerHTML;
      }
    } else if (kann && daten && daten.sections) {
      if (cart.classList) cart.classList.remove('is-empty');
      cart.renderContents(daten);
    }
    return daten;
  }

  // Auf der Warenkorbseite steht kein Knopf, aus dem sich die Variante ablesen
  // ließe — layout/theme.liquid legt sie deshalb global ab.
  const konfig = () => window.stoffeyaMuster || null;

  // Ein Umschlag, und nur einer. Zwei Dinge gehen hier schief, wenn niemand
  // aufräumt:
  //
  // 1. Der Mengenzähler im Warenkorb ließ sich hochstellen (gemessen: 7 ×
  //    „Stoffmuster" zu 0,00 € bei einmal 2,00 € Porto — sieben Umschläge gratis).
  // 2. Es können ZWEI Musterzeilen entstehen: Wer zwei Stoffe schnell
  //    hintereinander anklickt, löst zwei Anfragen aus, die beide „noch keine
  //    Zeile da" lesen und je eine anlegen. Shopify legt sie nicht zusammen, weil
  //    ihre Eigenschaften verschieden sind. Danach pflegt die Oberfläche nur die
  //    erste, die zweite fährt als Geisterzeile mit.
  //
  // Beides wird beim nächsten Seitenaufruf still geradegezogen: die Stoffe aller
  // Musterzeilen wandern in die erste (Reihenfolge bleibt, Doppelte fliegen raus,
  // bei der Höchstzahl wird gekappt), die überzähligen Zeilen verschwinden, und
  // die Menge steht wieder auf 1.
  //
  // 3. Ein gewählter Stoff kann inzwischen ausverkauft oder aus dem Shop
  //    genommen sein (gemessen: Bestellung #1183 mit einem Stoff im Entwurf).
  //    Er fliegt hier aus der Auswahl.
  async function ordneMusterzeile(cart) {
    const cfg = konfig();
    if (!cfg || !cart) return cart;
    const zeilen = (cart.items || []).filter((i) => Number(i.variant_id) === Number(cfg.variantId));
    if (!zeilen.length) return cart;

    const erste = zeilen[0];
    const ueberzaehlig = zeilen.slice(1);
    const alle = [];
    zeilen.forEach((z) => stoffeLesen(z).forEach((stoff) => {
      if (!alle.some((x) => x.handle === stoff.handle)) alle.push(stoff);
    }));
    const zuHaben = await Promise.all(alle.map((stoff) => nochZuHaben(stoff.handle)));
    const behalten = alle.filter((_, i) => zuHaben[i]).slice(0, Number(cfg.max) || 5);

    const istListe = stoffeLesen(erste).map((x) => x.handle).join('|');
    const sollListe = behalten.map((x) => x.handle).join('|');
    const altesFormat = String((erste.properties || {})[FELD] || '').includes('|');
    if (!ueberzaehlig.length && erste.quantity === 1 && istListe === sollListe && !altesFormat) return cart;

    try {
      // Zeilenschlüssel sind nicht durchnummeriert, das Entfernen einer Zeile
      // macht die Schlüssel der anderen also nicht ungültig.
      for (const z of ueberzaehlig) await schicke('/cart/change.js', { id: z.key, quantity: 0 }, true);
      if (erste.quantity !== 1 || istListe !== sollListe || altesFormat) {
        await schicke('/cart/change.js', behalten.length
          ? { id: erste.key, quantity: 1, properties: stoffeSchreiben(behalten) }
          : { id: erste.key, quantity: 0 }, true);
      }
      const frisch = await holeCart(true);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'stoffeya-muster', cartData: frisch });
      }
      // Knöpfe auf dieser Seite (Stoffseite, verwandte Produkte) nachziehen —
      // sonst stünde dort noch „ausgewählt" für einen eben entfernten Stoff.
      document.querySelectorAll('stoffeya-muster-button').forEach((el) => {
        if (typeof el.zeichne === 'function') el.zeichne(frisch);
      });
      return frisch;
    } catch (e) {
      return cart;
    }
  }

  // Die Mengenwahl blendet das Theme selbst aus (cart-item__quantity--muster),
  // hier bleibt nur der harte Riegel für Mengen, die anders entstanden sind.
  //
  // Immer nur ein Lauf zugleich. Das Skript ist `defer`: beim Ausführen steht
  // readyState schon auf „interactive", DOMContentLoaded kommt aber noch — beide
  // Aufrufe liefen los, lasen denselben Zeilenschlüssel und schrieben doppelt
  // (der zweite mit 400). Kommt während eines Laufs ein neuer Anlass (cartUpdate),
  // läuft danach genau einer nach.
  let wacheLauf = null;
  let wacheNochmal = false;
  function warenkorbWachen() {
    if (wacheLauf) {
      wacheNochmal = true;
      return wacheLauf;
    }
    wacheLauf = (async () => {
      try {
        let ergebnis;
        do {
          wacheNochmal = false;
          ergebnis = await ordneMusterzeile(await holeCart(true));
        } while (wacheNochmal);
        return ergebnis;
      } finally {
        wacheLauf = null;
      }
    })();
    return wacheLauf;
  }

  document.addEventListener('DOMContentLoaded', warenkorbWachen);
  if (document.readyState !== 'loading') warenkorbWachen();
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      // Nach dem Neuzeichnen des Warenkorbs ist das Mengenfeld ein neues Element.
      setTimeout(warenkorbWachen, 0);
    });
  }

  // Nach jedem Schreiben: Knöpfe auf den Stoffseiten nachziehen und dem Theme
  // sagen, dass sich der Warenkorb geändert hat.
  async function nachziehen() {
    const frisch = await holeCart(true);
    document.querySelectorAll('stoffeya-muster-button').forEach((el) => {
      if (typeof el.zeichne === 'function') el.zeichne(frisch);
    });
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'stoffeya-muster' });
    }
    return frisch;
  }

  // Einen einzelnen Stoff direkt im Warenkorb abwählen. Ohne das müsste, wer
  // sich bei einem von fünf vertut, den ganzen Umschlag löschen und die vier
  // richtigen noch einmal zusammensuchen.
  //
  // Der Griff geht über das Dokument, nicht über den Knopf: Die Schublade baut
  // ihre Zeilen nach jeder Änderung neu auf, ein direkt gebundener Zuhörer wäre
  // danach am alten, weggeworfenen Knopf.
  document.addEventListener('click', async (ereignis) => {
    const knopf = ereignis.target.closest('[data-muster-weg]');
    if (!knopf) return;
    ereignis.preventDefault();
    if (knopf.dataset.laeuft) return;
    knopf.dataset.laeuft = '1';
    try {
      const cfg = konfig();
      const zeile = zeileFinden(await holeCart(true), cfg && cfg.variantId);
      if (!zeile) return;
      const bleibt = stoffeLesen(zeile).filter((s) => s.handle !== knopf.dataset.musterWeg);
      await schicke('/cart/change.js', bleibt.length
        ? { id: zeile.key, quantity: 1, properties: stoffeSchreiben(bleibt) }
        : { id: zeile.key, quantity: 0 });   // der letzte Stoff: die Zeile hat keinen Zweck mehr
      await nachziehen();
    } catch (e) {
      /* Warenkorb bleibt, wie er war */
    } finally {
      delete knopf.dataset.laeuft;
    }
  });

  class MusterButton extends HTMLElement {
    connectedCallback() {
      this.knopf = this.querySelector('.muster__button');
      this.text = this.querySelector('.muster__text');
      this.hinweis = this.querySelector('.muster__hint');
      if (!this.knopf) return;
      this.variantId = Number(this.dataset.variant);
      this.max = Number(this.dataset.max) || 5;
      this.handle = this.dataset.handle;
      this.beschriftungen = {
        add: this.text.textContent.trim(),
        added: this.dataset.labelAdded || 'Muster ausgewählt',
        full: this.dataset.labelFull || 'Musterauswahl voll',
        hint: this.hinweis ? this.hinweis.textContent.trim() : '',
      };
      this.knopf.addEventListener('click', () => this.klick());
      holeCart().then((cart) => this.zeichne(cart));
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.abmelden = subscribe(PUB_SUB_EVENTS.cartUpdate, () =>
          holeCart(true).then((cart) => this.zeichne(cart))
        );
      }
    }

    disconnectedCallback() {
      if (typeof this.abmelden === 'function') this.abmelden();
    }

    zeichne(cart) {
      const stoffe = stoffeLesen(zeileFinden(cart, this.variantId));
      const drin = stoffe.some((s) => s.handle === this.handle);
      const voll = !drin && stoffe.length >= this.max;
      this.classList.toggle('muster--gewaehlt', drin);
      this.knopf.setAttribute('aria-pressed', drin ? 'true' : 'false');
      this.knopf.disabled = voll;
      this.text.textContent = drin
        ? this.beschriftungen.added
        : voll
          ? this.beschriftungen.full
          : this.beschriftungen.add;
      if (this.hinweis) {
        this.hinweis.textContent = stoffe.length
          ? `${stoffe.length} von ${this.max} — ${this.beschriftungen.hint}`
          : this.beschriftungen.hint;
      }
    }

    async klick() {
      if (this.laeuft) return;
      this.laeuft = true;
      this.knopf.classList.add('muster__button--laeuft');
      try {
        const cart = await holeCart(true);
        const zeile = zeileFinden(cart, this.variantId);
        let stoffe = stoffeLesen(zeile);
        const drin = stoffe.some((s) => s.handle === this.handle);

        if (drin) {
          stoffe = stoffe.filter((s) => s.handle !== this.handle);
        } else {
          if (stoffe.length >= this.max) return;
          stoffe.push({ handle: this.handle, label: this.dataset.label || this.handle, sku: this.dataset.sku || '' });
        }

        if (!zeile) {
          await schicke('/cart/add.js', {
            items: [{ id: this.variantId, quantity: 1, properties: stoffeSchreiben(stoffe) }],
          });
        } else if (stoffe.length) {
          // Eigenschaften lassen sich nur beim Ändern der ganzen Zeile setzen;
          // Shopify vergibt dabei einen neuen `key`, deshalb danach neu lesen.
          await schicke('/cart/change.js', { id: zeile.key, quantity: 1, properties: stoffeSchreiben(stoffe) });
        } else {
          // Letzter Stoff abgewählt: die Zeile hat keinen Zweck mehr.
          await schicke('/cart/change.js', { id: zeile.key, quantity: 0 });
        }

        await nachziehen();
      } catch (e) {
        if (this.hinweis) this.hinweis.textContent = this.dataset.labelError || 'Das hat gerade nicht geklappt.';
      } finally {
        this.laeuft = false;
        this.knopf.classList.remove('muster__button--laeuft');
      }
    }
  }

  if (!customElements.get('stoffeya-muster-button')) {
    customElements.define('stoffeya-muster-button', MusterButton);
  }
})();
